import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Smartphone, Building2, Copy, Check, AlertCircle } from 'lucide-react';

import { tenantService } from '../../services/api';

// Must match the name the CBE/Telebirr accounts are actually registered under,
// or guests' transfers will be rejected by the bank.
const CBE_ACCOUNT = '1000123456789';
const TELEBIRR_PHONE = '0912345678';
const ACCOUNT_NAME = 'Zaff Software';

function formatPrice(amount) {
  return new Intl.NumberFormat('en-ET', {
    style: 'currency',
    currency: 'ETB',
    maximumFractionDigits: 0,
  }).format(amount).replace('ETB', 'ብር');
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const [method, setMethod] = useState(null); // 'cbe' | 'telebirr'
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const raw = sessionStorage.getItem('pendingPayment');
    if (!raw) { navigate('/register', { replace: true }); return; }
    setPending(JSON.parse(raw));
  }, [navigate]);

  if (!pending) { return null; }

  const subtotal = pending.roomCount * pending.pricePerRoom;
  const vat = Math.round(subtotal * 0.15);
  const total = subtotal + vat;

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!method) { setError('Please select a payment method.'); return; }
    if (!reference.trim()) { setError('Please enter your transaction reference number.'); return; }
    setError('');
    setSubmitting(true);
    try {
      console.log('Submitting payment reference for tenant:', pending.tenantId);
      await tenantService.submitPaymentReference({
        tenantId: pending.tenantId,
        paymentMethod: method,
        paymentReference: reference.trim(),
      });
      sessionStorage.removeItem('pendingPayment');
      setSubmitted(true);
    } catch (err) {
      console.error('Payment submission error:', err);
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-primary-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full space-y-6">
          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
              <CheckCircle className="w-14 h-14 text-white" />
            </div>
          </div>

          {/* Main Message */}
          <div className="text-center space-y-3">
            <h1 className="text-3xl font-bold text-gray-900">Application Received!</h1>
            <p className="text-lg text-gray-600">
              Thank you, <strong>{pending.adminEmail?.split('@')[0]}</strong>!
            </p>
          </div>

          {/* What Happens Next */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              What happens next?
            </h2>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                <span>Our team will <strong>verify your payment</strong> within the next few hours (typically within 1 business day).</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                <span>Once confirmed, we will <strong>activate your hotel dashboard</strong> immediately.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                <span>You will receive an <strong>activation email</strong> at <strong className="text-blue-700">{pending.adminEmail}</strong> with your login credentials and next steps.</span>
              </li>
            </ol>
          </div>

          {/* Payment Details Summary */}
          <div className="bg-gray-50 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Payment Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Hotel Name:</span>
                <span className="font-semibold text-gray-900">{pending.tenantName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription Plan:</span>
                <span className="font-semibold text-gray-900">{pending.planName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-semibold text-green-700">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Method:</span>
                <span className="font-semibold text-gray-900">{method === 'cbe' ? 'CBE Birr' : 'Telebirr'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Reference Number:</span>
                <span className="font-mono font-semibold text-gray-900">{reference}</span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Questions or need help? Contact us at{' '}
              <a href="mailto:support@zaffsoftware.com" className="text-primary-600 font-medium hover:underline">
                support@zaffsoftware.com
              </a>
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Please keep this reference number for your records: <span className="font-mono font-semibold">{reference}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-green-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="bg-primary-900 px-6 py-5 text-white">
          <p className="text-primary-300 text-xs font-semibold uppercase tracking-widest mb-1">Final step</p>
          <h1 className="text-xl font-bold">Complete your payment</h1>
          <p className="text-primary-200 text-sm mt-1">{pending.tenantName} - {pending.planName} plan</p>
        </div>

        <div className="p-6 space-y-6">

          {/* Amount breakdown */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Amount Due (Monthly)</p>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{pending.roomCount} rooms × {formatPrice(pending.pricePerRoom)}</span>
              <span className="font-medium text-gray-900">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>15% VAT</span>
              <span className="font-medium text-gray-900">{formatPrice(vat)}</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2 mt-1">
              <span className="text-gray-900">Total</span>
              <span className="text-primary-700">{formatPrice(total)}</span>
            </div>
          </div>

          {/* Payment method selection */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-800">Choose payment method</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMethod('cbe')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  method === 'cbe' ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className={`w-6 h-6 ${method === 'cbe' ? 'text-primary-700' : 'text-gray-500'}`} />
                <span className={`text-sm font-semibold ${method === 'cbe' ? 'text-primary-700' : 'text-gray-700'}`}>CBE Birr</span>
                <span className="text-xs text-gray-400">Commercial Bank</span>
              </button>
              <button
                type="button"
                onClick={() => setMethod('telebirr')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  method === 'telebirr' ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Smartphone className={`w-6 h-6 ${method === 'telebirr' ? 'text-primary-700' : 'text-gray-500'}`} />
                <span className={`text-sm font-semibold ${method === 'telebirr' ? 'text-primary-700' : 'text-gray-700'}`}>Telebirr</span>
                <span className="text-xs text-gray-400">Ethio Telecom</span>
              </button>
            </div>
          </div>

          {/* Payment instructions */}
          {method && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3 animate-fade-in">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Payment instructions</p>

              {method === 'cbe'
                ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-700">Transfer <strong>{formatPrice(total)}</strong> to the following CBE account:</p>
                    <div className="bg-white rounded-lg px-4 py-3 border border-blue-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Account Number</p>
                        <p className="font-bold text-gray-900 text-base tracking-widest">{CBE_ACCOUNT}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Account Name: <span className="font-medium text-gray-700">{ACCOUNT_NAME}</span></p>
                      </div>
                      <button type="button" onClick={() => copyText(CBE_ACCOUNT, 'cbe')} className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                        {copied === 'cbe' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                    <p className="text-xs text-blue-600">Use your hotel name <strong>"{pending.tenantName}"</strong> as the transfer description.</p>
                  </div>
                )
                : (
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-700">Send <strong>{formatPrice(total)}</strong> via Telebirr to:</p>
                    <div className="bg-white rounded-lg px-4 py-3 border border-blue-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Phone Number</p>
                        <p className="font-bold text-gray-900 text-base tracking-widest">{TELEBIRR_PHONE}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Account Name: <span className="font-medium text-gray-700">{ACCOUNT_NAME}</span></p>
                      </div>
                      <button type="button" onClick={() => copyText(TELEBIRR_PHONE, 'telebirr')} className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                        {copied === 'telebirr' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                    <p className="text-xs text-blue-600">Use your hotel name <strong>"{pending.tenantName}"</strong> as the transfer note.</p>
                  </div>
                )}
            </div>
          )}

          {/* Reference input */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-1">
                Enter your payment reference number
              </label>
              <input
                type="text"
                value={reference}
                onChange={e => setReference(e.target.value)}
                placeholder="e.g. FT2507132345678"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">Copy it from your Telebirr or CBE Birr confirmation message, paste it here, then tap "I have paid" below.</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !reference.trim()}
              className="w-full bg-ethiopian-green text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting
                ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting...</>
                )
                : (
                  <><CheckCircle className="w-4 h-4" /> I have paid - submit reference</>
                )}
            </button>
          </form>

          <p className="text-xs text-center text-gray-400">
            Need help? Contact us at <a href="mailto:support@zaffsoftware.com" className="text-primary-600 hover:underline">support@zaffsoftware.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
